import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getUserInfo } from 'ui-addon-upgrades/_core/common/api/getUserInfo';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import Raven from 'raven-js';
import withErrorBoundary from '../../ErrorBoundary';
import { RequestAccessModalWrapper } from '../../RequestAccessModalWrapper';
import { isAdmin } from '../../_core/common/eligibility/isAdmin';
import { navigateTo, openNewTab } from '../../_core/common/navigation/url';
import { requestSKU } from '../../_core/upgrade/requestSKU';
import { isQuantityPackApiName } from '../../_core/utils/productUtils';
import { useGlobalAlertStore } from '../../_core/common/GlobalAlertManager';
import { generateCheckoutUrl } from './buy-now-api';
import { getBuyNowUsageTrackingProperties, trackClickedBuyNowButton, trackViewBuyNowButton } from './buy-now-trackers';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const _UIBuyNowButton = ({
  block,
  children,
  currencyCode,
  'data-test-id': dataTestId,
  'data-selenium-id': dataSeleniumId,
  disabled = false,
  FailedPurchaseOrderCallToAction,
  onBuyNowFailure,
  onClick,
  requestedProducts,
  shouldDisplayAlertOnFailure = false,
  size,
  terms,
  trackingProperties,
  use = 'primary',
  userInfo: passedUserInfo,
  redirectQueryParams,
  openCheckoutInNewTab = false
}) => {
  const buyNowUsageTrackingProperties = getBuyNowUsageTrackingProperties(trackingProperties);
  const requestAccessModalTrackingProperties = Object.assign({}, buyNowUsageTrackingProperties, {
    modalType: 'requestSKU'
  });
  const {
    store: globalAlertStore,
    shouldRenderGlobalList
  } = useGlobalAlertStore();
  const [userInfo, setUserInfo] = useState(passedUserInfo);
  const [isLoadingUserState, setIsLoadingUserState] = useState(!passedUserInfo);
  const [isGeneratingPurchaseOrderId, setIsGeneratingPurchaseOrderId] = useState(false);
  const hasPermissionToBuyNow = useMemo(() => {
    return !!userInfo && isAdmin(userInfo.user, 'billing');
  }, [userInfo]);
  const foundApiName = requestedProducts === null || requestedProducts === void 0 ? void 0 : requestedProducts.map(({
    productApiName
  }) => productApiName).find(apiName => !isQuantityPackApiName(apiName));
  const handleRequestAccess = useCallback(() => {
    if (!foundApiName) {
      return Promise.reject(new Error('No product api name provided to requestSKU'));
    }
    return requestSKU({
      apiName: foundApiName
    });
  }, [foundApiName]);
  const handleClickBuyNow = useCallback(() => {
    setIsGeneratingPurchaseOrderId(true);
    if (onClick) {
      onClick();
    }
    trackClickedBuyNowButton(buyNowUsageTrackingProperties);
    generateCheckoutUrl({
      buyNowUsageTrackingProperties,
      currencyCode,
      requestedProducts,
      terms
    }).then(checkoutUrl => {
      if (!checkoutUrl) {
        Raven.captureException(new Error('No checkout url provided from generateCheckoutUrl'));
        return;
      }
      const url = new URL(checkoutUrl);

      // add any redirect params to the checkout URL if provided
      if (redirectQueryParams) {
        Object.entries(redirectQueryParams).forEach(([key, value]) => {
          url.searchParams.append(key, value.toString());
        });
      }
      if (openCheckoutInNewTab) {
        openNewTab(url.href);
      } else {
        navigateTo(url.href);
      }
    }).catch(error => {
      if (shouldDisplayAlertOnFailure) {
        globalAlertStore.addAlert({
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "upgrades.buyNowFailureAlert.title"
          }),
          message: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "upgrades.buyNowFailureAlert.message"
            }), FailedPurchaseOrderCallToAction || null]
          }),
          type: 'danger'
        });
      }
      if (onBuyNowFailure) {
        onBuyNowFailure(error);
      }
    }).finally(() => {
      setIsGeneratingPurchaseOrderId(false);
    });
  }, [onClick, buyNowUsageTrackingProperties, currencyCode, requestedProducts, terms, redirectQueryParams, openCheckoutInNewTab, shouldDisplayAlertOnFailure, onBuyNowFailure, globalAlertStore, FailedPurchaseOrderCallToAction]);
  useEffect(() => {
    if (!passedUserInfo) {
      void getUserInfo().then(setUserInfo).finally(() => setIsLoadingUserState(false));
    }
  }, [passedUserInfo]);
  useEffect(() => {
    trackViewBuyNowButton(buyNowUsageTrackingProperties);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (isLoadingUserState) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [shouldRenderGlobalList && /*#__PURE__*/_jsx(FloatingAlertList, {
      "data-test-id": "global-floating-alert-list",
      alertStore: globalAlertStore
    }), hasPermissionToBuyNow ? /*#__PURE__*/_jsx(LoadingButton, {
      block: block,
      "data-test-id": dataTestId || 'buy-now-button',
      "data-selenium-id": dataSeleniumId,
      disabled: disabled,
      loading: isGeneratingPurchaseOrderId,
      onClick: handleClickBuyNow,
      size: size,
      use: use,
      children: children || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.checkoutButton.default"
      })
    }) : /*#__PURE__*/_jsx(RequestAccessModalWrapper, {
      buttonBlock: block,
      buttonDisabled: disabled,
      buttonSize: size,
      buttonSeleniumId: dataSeleniumId,
      buttonTestId: dataTestId || 'buy-now-button',
      buttonUse: use,
      floatingAlertStore: globalAlertStore,
      requestAccessPromise: handleRequestAccess,
      usageTrackingProperties: requestAccessModalTrackingProperties,
      OpenModalButtonLabel: children || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.checkoutButton.default"
      }),
      ModalBody: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.requestAccessModalWrapper.buyNow.description"
      }),
      ModalTitle: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.requestAccessModalWrapper.buyNow.title"
      }),
      modalUse: "success",
      modalWidth: 540,
      SubmitRequestButtonLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "upgrades.requestAccessModalWrapper.buyNow.button"
      })
    })]
  });
};
export const UIBuyNowButton = withErrorBoundary(_UIBuyNowButton, {
  boundaryName: 'UIBuyNowButton'
});